/**
 * Tier-3 — the member's own usage view.
 *
 * No capability gate: every member may see what they personally spent and
 * which cap is funding their next dollar. The endpoint is self-scoped from
 * auth (there is no email parameter), so this cannot be pointed at anyone
 * else.
 *
 * The interesting part is the attribution waterfall. Enforcement pools
 * personal + team + role into ONE ceiling, so a member is never
 * interrupted when a single component runs dry. `attributeUsage` replays
 * that single number across its sources for display, and `activeSource`
 * is "where the next dollar comes from" — highlighted, because
 * "personal is spent, I'm on the team budget now" is the question this
 * view exists to answer.
 *
 * ── Personal drives (`personal`) ──
 * The same endpoint serves a personal tenant (membership is the only gate and
 * the owner has one), but the waterfall does NOT apply there: role/team/member
 * allocation is an org concept, so the server returns personal 0 / team 0 /
 * role null and the table would read "personal exhausted, team exhausted,
 * drawing from your Role budget" — three lies about a drive with no roles.
 * What actually governs a solo user is the PLAN's included allowance
 * (`tenantFreeCap`) and how much of it is used. So `personal` swaps the
 * waterfall for that one figure plus its meter, and drops the Enforcement
 * section, whose copy is entirely about org-internal caps behaving as advisory.
 * The extra (overage) allowance `tenantExtraCap` is deliberately NOT shown to
 * customers any more (2026-08-19); the wire still carries it.
 *
 * ── Spend by feature (A5) ──
 * The waterfall and the buckets both answer where the money came FROM. Until
 * A5 nothing here answered where it WENT, so a member who had run out could see
 * that they had and not what had consumed it. `featureBreakdown` is the same
 * aggregation the org dashboard renders, narrowed server-side to the caller —
 * one implementation, so a member's rows sum to their slice of the org's. The
 * component is shared unchanged; only the section copy differs.
 *
 * ── One page, not two (2026-08-12) ──
 * This took a `variant` prop ('usage' | 'spending') because it backed BOTH
 * Organization → Spending and Account → Usage, and without the prop the two
 * nav items rendered byte-identical pages. That was a fix for a symptom: one
 * subject was filed twice, each copy showing a strict subset of the other.
 *
 * The nav items are merged and the prop is gone, so everything here renders
 * unconditionally — the attribution waterfall AND spend by bucket AND spend by
 * feature. A member who only ever opened Spending previously never saw the
 * first two, which are the answer to "why did my calls stop".
 *
 * `/settings/usage` still resolves here; only the second door was removed.
 */
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useRefreshWhenShown } from '@papan/core-ui/ui'
import { ChartRenderer } from '@papan/charts/ui'
import { budgetApi, type MyUsageResponse } from '../../client/budget'
import { bucketApplies } from './logic'
import { MonthPicker, periodKey } from './MonthPicker'
import {
  useMoney, MoneyText, CapText, Band, StatTile,
  EnforcementNote, UsageMeter, FeatureBreakdown, useFeatureChart,
} from './bits'

export function MemberUsageView({
  tenantId,
  personal,
}: {
  tenantId?: string
  personal?: boolean
}) {
  const { t } = useTranslation('core-ui')
  const [monthPeriod, setMonthPeriod] = useState(() => periodKey(new Date()))
  const [data, setData] = useState<MyUsageResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  /**
   * Re-fetch when the deck comes back on screen.
   *
   * The desktop shell keeps a deck MOUNTED behind `display: none`, so the
   * effect below is otherwise the only fetch this page ever runs — it served
   * whatever was true the first time Settings was opened, with no error and no
   * way back but a full page reload. That was the reported symptom on
   * Settings → Spending (2026-08-13).
   *
   * A counter in the dependency list rather than a hoisted `load` callback:
   * the fetch is written inline with an `alive` guard against out-of-order
   * responses, and hoisting it would move that guard for no gain.
   */
  const [reload, setReload] = useState(0)
  useRefreshWhenShown(() => setReload(n => n + 1))

  useEffect(() => {
    if (!tenantId) return
    let alive = true
    setLoading(true)
    budgetApi.getMyUsage(tenantId, monthPeriod)
      .then(d => { if (alive) { setData(d); setError(null) } })
      .catch((e: any) => { if (alive) setError(e?.message || 'Failed to load usage') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [tenantId, monthPeriod, reload])

  const money = useMoney()
  // Unfiltered, like the org dashboard. The withholding that used to sit here
  // was for a metered-but-unpriced `storage` row; 0171 removed the meter.
  const featureRows = useMemo(() => data?.featureBreakdown ?? [], [data])
  // Same hook the org dashboard uses, so one figure cannot be charted two ways.
  const featureChart = useFeatureChart(featureRows, t)

  if (!tenantId) return <div className="p-4 themed-text-muted text-sm">{t('settings.budget.noTenant')}</div>
  if (loading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
  if (error || !data) return <div className="p-4 text-sm text-red-400">{error}</div>

  const { usage, attribution, caps } = data
  // Personal mode only: a finite, non-zero included allowance is the one thing
  // there is a meaningful bar to fill. `null` = unlimited, `0` = hard-blocked
  // — neither has a fill fraction (same rule as the org dashboard's meter).
  const planMeter = caps.tenantFreeCap != null && caps.tenantFreeCap > 0
  const sourceLabel = (s: string) =>
    s === 'personal' ? t('settings.budget.source.personal')
      : s === 'team' ? t('settings.budget.source.team')
        : t('settings.budget.source.role')

  return (
    <div className="max-w-3xl">
      {/* The heading follows the VARIANT, not the component. Landing on a page
          headed "Usage" after clicking a sidebar item labelled "Spending" is
          the mismatch a member reported, and it applied to every caller this
          view served on that route. */}
      <div className="flex items-start justify-between gap-4">
        <h2 className="themed-text text-lg font-medium mb-1">{t('settings.budget.dashboard.title')}</h2>
        {/* Same control the org dashboard carries — a member has the same
            question after an invoice, about their own slice. */}
        <MonthPicker value={monthPeriod} onChange={setMonthPeriod} />
      </div>
      <p className="themed-text-muted text-sm mb-4">
        {t('settings.budget.dashboard.descSelf', { period: data.period })}
      </p>

      {/* ── EVERY FIGURE BELOW IS ABOUT A WALLET THIS VIEWER IS NOT ON ──
          A seated platform administrator is enforced against the PLATFORM
          org's caps and pools, while this page reads the customer's: their
          own spend is payer-keyed and so reads 0, the headroom bar reads
          full, and the waterfall names allowances that will never gate them
          — including when the platform org is exhausted and their calls have
          actually stopped, which is the question this view exists to answer.
          It cannot show the platform's numbers instead (cross-tenant, and
          this caller holds no capability there), so it says so. */}
      {data.platformSeat && (
        <div className="mb-4 rounded border border-sky-500/30 bg-sky-500/10 px-3 py-2">
          <p className="themed-text text-sm">
            {t('settings.budget.platformSeatSelf', {
              defaultValue: 'You are on a platform support seat in this organization.',
            })}
          </p>
          <p className="themed-text-muted text-xs mt-1">
            {t('settings.budget.platformSeatSelfHint', {
              defaultValue: 'Your AI usage here is billed to the platform, not to this organization — so it does not appear in the figures below, and the allowances shown are this organization’s rather than the ones your usage is checked against.',
            })}
          </p>
        </div>
      )}

      {personal ? (
        <>
          {/* Plan allowance, not the org waterfall — see the header comment.
              Free allowance + utilisation, first and prominent. The extra
              (overage) allowance is no longer shown to customers (2026-08-19);
              the wire still carries `caps.tenantExtraCap`. */}
          <Band title={t('settings.budget.includedAllowance')} info={t('settings.budget.includedAllowanceHint')}>
            {/* Just the utilisation bar; the spend + allowance tiles that used
                to sit here restated it (2026-08-19). Lede is the fallback for
                the states a bar cannot show: blocked (0) and unlimited (null). */}
            {planMeter ? (
              <UsageMeter
                label={t('settings.budget.meterFree')}
                usedMicro={usage.free}
                capMicro={caps.tenantFreeCap as number}
                money={money}
              />
            ) : (
              <div className="text-sm themed-text">
                {caps.tenantFreeCap === 0
                  ? <span className="text-red-400">{t('settings.budget.lede.blocked')}</span>
                  : t('settings.budget.lede.none', { used: money.fmt(usage.total) })}
              </div>
            )}
          </Band>
        </>
      ) : (
        <>
          {/* Your allowance + utilisation, first. The pooled ceiling is the
              member's own free allowance (personal + team + role), not an
              overage limit. */}
          <Band title={t('settings.budget.pooledCeiling')} info={t('settings.budget.pooledCeilingHint')}>
            {/* The utilisation bar carries spend-vs-ceiling; the spend and
                ceiling tiles that duplicated it were dropped (2026-08-19). Lede
                is the fallback for the states a bar cannot show. */}
            {attribution.totalCap != null && attribution.totalCap > 0 ? (
              <UsageMeter
                label={t('settings.budget.meterPooled')}
                usedMicro={usage.total}
                capMicro={attribution.totalCap}
                money={money}
              />
            ) : (
              <div className="text-sm themed-text">
                {attribution.totalCap === 0
                  ? <span className="text-red-400">{t('settings.budget.lede.blocked')}</span>
                  : t('settings.budget.lede.none', { used: money.fmt(usage.total) })}
              </div>
            )}

            {/* Where the next dollar is drawn from — the one figure the bar
                cannot show, kept as a single tile. */}
            <div className="mt-3 flex">
              <StatTile
                label={t('settings.budget.nextDollarFrom')}
                value={attribution.activeSource
                  ? <span className="text-emerald-400">{sourceLabel(attribution.activeSource)}</span>
                  : <span className="text-red-400">{t('settings.budget.exhausted')}</span>}
                hint={t('settings.budget.nextDollarHint')}
              />
            </div>
          </Band>

          {/* The "which pool is funding me" answer. It used to be gated to the
              `usage` variant, which is what a member lost if they only ever
              opened Spending — so it is unconditional now that the two pages
              are one. */}
          <Band title={t('settings.budget.breakdown.title')} info={t('settings.budget.breakdown.desc')} card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b themed-border">
                    <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.source')}</th>
                    <th className="text-right px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.used')}</th>
                    <th className="text-right px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.cap')}</th>
                    <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {attribution.rows.map(row => {
                    const active = row.source === attribution.activeSource
                    return (
                      <tr
                        key={row.source}
                        className={`border-b themed-border last:border-b-0 ${active ? 'bg-emerald-500/10' : ''}`}
                      >
                        <td className="px-4 py-2.5 themed-text">
                          {active && <span className="text-emerald-400 mr-1.5" aria-hidden>▸</span>}
                          {sourceLabel(row.source)}
                          {active && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                              {t('settings.budget.drawingNow')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right"><MoneyText micro={row.used} money={money} className="themed-text" /></td>
                        {/* Every row here is an additive CONTRIBUTION to one
                            pooled ceiling, not a ceiling of its own — so a 0
                            means "adds nothing", never the hard block a 0
                            *ceiling* is. Showing it as a red "Blocked" chip
                            contradicted the spending-limits editor, which says
                            in as many words that a source set to 0 "contributes
                            nothing, it does not block anyone", and it alarmed
                            people whose calls were funding fine off the role
                            allowance. Same reason "Exhausted" is wrong below: a
                            source that never contributed has not run out. */}
                        <td className="px-4 py-2.5 text-right"><CapText cap={row.cap} money={money} zeroIsBlock={false} /></td>
                        <td className="px-4 py-2.5 text-xs">
                          {row.cap === 0
                            ? (
                              <span className="themed-text-muted" title={t('settings.budget.contributesNothingHint')}>
                                {t('settings.budget.contributesNothing')}
                              </span>
                            )
                            : row.exhausted
                              ? <span className="themed-text-muted">{t('settings.budget.exhausted')}</span>
                              : <span className="text-emerald-400">{t('settings.budget.available')}</span>}
                        </td>
                      </tr>
                    )
                  })}
                  {/* A negative member adjustment is not a drawable source — it
                      reduces the pool. Rendering it as a row would produce a
                      nonsensical "personal $0 / −$9". */}
                  {attribution.reduction > 0 && (
                    <tr className="border-t themed-border">
                      <td className="px-4 py-2.5 themed-text-muted text-xs" colSpan={2}>
                        {t('settings.budget.adjustmentReduction')}
                      </td>
                      <td className="px-4 py-2.5 text-red-400 text-sm" colSpan={2}>
                        −<MoneyText micro={attribution.reduction} money={money} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Band>
        </>
      )}

      {/* What the caller actually spent it on — the same section an admin
          reads on the org dashboard, self-scoped. This is the whole reason
          Spending is a different page from Usage for a member: without it the
          two rendered identically. Storage is absent BY CONSTRUCTION — 0171
          removed the meter entirely, so no `storage` row is written at all;
          drive utilisation lives on Settings → Storage, beside the quota it is
          checked against. (This used to claim it was absent "twice over", the
          second guarantee being a customer-facing filter — which the same
          commit that removed the meter also deleted. `featureRows` above is
          unfiltered. Anyone re-metering storage should read that as: nothing
          here is holding it back.) */}
      <Band title={t('settings.budget.feature.title')} info={t('settings.budget.feature.desc')} card>
        <FeatureBreakdown rows={featureRows} money={money} />
      </Band>

      {/* The same pie the org dashboard draws, through the same hook, so the
          two cannot chart one figure differently. Only the FEATURE chart is
          here — by-member and by-team pies over a set of one are circles, and
          the ORG_BILLING_UX plan reaches that conclusion too. Absent entirely
          when nothing is chargeable, since a cost-only pie of all-zero
          features has no slices to draw. */}
      {featureChart && (
        <Band title={t('settings.budget.charts.title')}>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[280px] themed-bg-secondary rounded-lg p-3">
              <div className="themed-text-muted text-xs mb-2">{t('settings.budget.feature.title')}</div>
              <ChartRenderer config={featureChart} />
            </div>
          </div>
        </Band>
      )}

      {/* Where the money came from — included allowance, promo grants and
          prepaid credit. Overage buckets (extra/grace) are intentionally not
          shown to customers (2026-08-19); the wire still carries them. */}
      <Band title={t('settings.budget.buckets.title')} info={t(personal ? 'settings.budget.buckets.personalDesc' : 'settings.budget.buckets.desc')}>
        {/* All three customer-facing buckets always render, in waterfall order
            (promo → free → prepaid). `free` always shows a value. Promo and
            prepaid show 0.00 when the tenant HAS that source (applicable) and
            '—' when it does not — so a fresh applicable tenant reads 0.00, not a
            misleading blank, and a tenant with no promo/credits reads '—' rather
            than a phantom 0.00. A bucket that actually funded something always
            shows its amount (the `spend > 0` OR), and an older API that omits
            the flags falls back to showing the value. (Overage buckets
            extra/grace stay hidden; the wire still carries them.) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatTile
            label={t('settings.budget.bucket.promo')}
            value={bucketApplies(data.promoApplicable, usage.promo)
              ? <MoneyText micro={usage.promo ?? 0} money={money} />
              : '—'}
            hint={t('settings.budget.bucket.promoHint')}
          />
          <StatTile label={t('settings.budget.bucket.free')} value={<MoneyText micro={usage.free} money={money} />} hint={t('settings.budget.bucket.freeHint')} />
          <StatTile
            label={t('settings.budget.bucket.prepaid')}
            value={bucketApplies(data.prepaidApplicable, usage.prepaid)
              ? <MoneyText micro={usage.prepaid ?? 0} money={money} />
              : '—'}
            hint={t('settings.budget.bucket.prepaidHint')}
          />
        </div>
      </Band>

      {/* The `usage`-variant copy of "Spend by feature" lived here.
          Both variants rendered this section, and merging them would have
          drawn it TWICE — the same numbers under the same heading a few
          hundred pixels apart, plus two `settings-spy-*` anchors with one
          name, so the scroll-spy listed the section twice and one entry could
          never win. The copy above (with its own description and the chart an
          admin gets) is the one that survives. */}

      {!personal && (
        <Band title={t('settings.budget.enforcement.title')} card>
          <EnforcementNote mode={data.enforcementMode} />
        </Band>
      )}
    </div>
  )
}
